/**
 * Manual journals, from draft to the ledger.
 *
 * The Book Keeper submits a journal, a Finance Head approves it, and the Book
 * Keeper posts it. Journals had an API and no screen, so a draft nobody had
 * typed in here sat with nowhere to go. The checker's step is the same one the
 * Approvals screen takes; the server refuses anybody approving what they
 * submitted, whichever screen they use.
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, formatInr } from '../api'
import { useAuth } from '../auth'
import { Card, Empty, ErrorNote, Loading, Note, Pill } from '../components/ui'

interface JournalLine {
  line_no: number
  account_code: string | null
  account_name: string | null
  location_name: string | null
  debit_paise: number
  credit_paise: number
  memo: string | null
}

interface Journal {
  id: string
  reference: string
  journal_date: string
  narration: string
  status: 'draft' | 'submitted' | 'rejected' | 'posted' | 'withdrawn'
  amount_paise: number
  approval_state: string
  approved: boolean
  sent_back_reason: string | null
  prepared_by: string | null
  journal_number: string | null
  lines: JournalLine[]
}

/** Where a journal stands, as the person who has to act next reads it. */
function stage(j: Journal): { pill: string; text: string } {
  if (j.status === 'posted') return { pill: 'posted', text: `in the books as ${j.journal_number}` }
  if (j.approved) return { pill: 'approved', text: 'approved — the Book Keeper posts it' }
  if (j.status === 'submitted') return { pill: 'submitted', text: 'with the Finance Head' }
  if (j.status === 'rejected')
    return { pill: 'rejected', text: 'rejected — correct and submit again' }
  if (j.status === 'withdrawn') return { pill: 'withdrawn', text: 'withdrawn' }
  return { pill: 'draft', text: 'draft — the Book Keeper submits it' }
}

const FILTERS = [
  { key: 'open', label: 'To act on' },
  { key: 'posted', label: 'Posted' },
  { key: 'all', label: 'All' },
] as const

export default function Journals() {
  const [filter, setFilter] = useState<(typeof FILTERS)[number]['key']>('open')
  const { data, isLoading, error } = useQuery({
    queryKey: ['journals'],
    queryFn: () => api.get<Journal[]>('/journals'),
  })

  if (isLoading) return <Loading what="Loading journals" />
  const all = data ?? []
  // Work to do reads oldest first, in the order it has to be posted; what is
  // already in the books reads newest first.
  const byDate = (a: Journal, b: Journal) =>
    a.journal_date.localeCompare(b.journal_date) || a.reference.localeCompare(b.reference)
  const rows = all
    .filter((j) =>
      filter === 'all'
        ? true
        : filter === 'posted'
          ? j.status === 'posted'
          : j.status !== 'posted' && j.status !== 'withdrawn',
    )
    .sort((a, b) => (filter === 'open' ? byDate(a, b) : byDate(b, a)))
  const waiting = all.filter((j) => j.status !== 'posted' && j.status !== 'withdrawn').length

  return (
    <>
      <ErrorNote error={error} />
      <div className="row" role="tablist" aria-label="Which journals">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            role="tab"
            aria-selected={filter === f.key}
            className={filter === f.key ? 'primary' : ''}
            onClick={() => setFilter(f.key)}
          >
            {f.label}
            {f.key === 'open' && waiting > 0 ? ` (${waiting})` : ''}
          </button>
        ))}
      </div>
      {rows.length === 0 ? (
        <Card>
          <Empty>
            {filter === 'open' ? 'Nothing waiting: every journal is posted.' : 'No journals here.'}
          </Empty>
        </Card>
      ) : (
        rows.map((j) => <JournalCard key={j.id} journal={j} />)
      )}
      <p className="faint">
        A journal goes draft → submitted → approved → posted. The Book Keeper submits and posts; a
        Finance Head approves, here or on <Link to="/books/approvals">Approvals</Link>, and cannot
        approve a journal they submitted.
      </p>
    </>
  )
}

function JournalCard({ journal: j }: { journal: Journal }) {
  const { can } = useAuth()
  const queryClient = useQueryClient()
  const [sendBack, setSendBack] = useState('')
  const act = useMutation({
    mutationFn: ({ path, body }: { path: string; body?: unknown }) => api.post(path, body),
    onSuccess: () => {
      setSendBack('')
      void queryClient.invalidateQueries({ queryKey: ['journals'] })
      void queryClient.invalidateQueries({ queryKey: ['approvals'] })
    },
  })
  const s = stage(j)
  const debits = j.lines.reduce((sum, l) => sum + l.debit_paise, 0)
  const credits = j.lines.reduce((sum, l) => sum + l.credit_paise, 0)
  const canSubmit = can('gl.journal.submit') && (j.status === 'draft' || j.status === 'rejected')
  const canPost = can('gl.journal.create') && j.approved
  const canDecide = can('gl.journal.approve') && j.status === 'submitted' && !j.approved

  return (
    <Card
      title={
        <>
          <span className="mono">{j.reference}</span> · {j.journal_date} ·{' '}
          {formatInr(j.amount_paise)}
        </>
      }
      hint={
        <>
          <Pill value={s.pill} /> {s.text}
        </>
      }
      tight
    >
      <div style={{ padding: '8px 12px' }}>
        <div>{j.narration}</div>
        {j.prepared_by && <div className="faint">Prepared by {j.prepared_by}</div>}
        {j.sent_back_reason && <Note kind="warn">Came back: {j.sent_back_reason}</Note>}
      </div>
      <table>
        <thead>
          <tr>
            <th>Account</th>
            <th>Clinic</th>
            <th className="num">Debit</th>
            <th className="num">Credit</th>
            <th>Memo</th>
          </tr>
        </thead>
        <tbody>
          {j.lines.map((l) => (
            <tr key={l.line_no}>
              <td>
                <span className="mono">{l.account_code}</span> {l.account_name}
              </td>
              <td className="muted">{l.location_name ?? <span className="faint">—</span>}</td>
              <td className="num">{l.debit_paise ? formatInr(l.debit_paise) : ''}</td>
              <td className="num">{l.credit_paise ? formatInr(l.credit_paise) : ''}</td>
              <td className="muted">{l.memo}</td>
            </tr>
          ))}
          <tr>
            <td colSpan={2}>
              <b>Total</b>
            </td>
            <td className="num">
              <b>{formatInr(debits)}</b>
            </td>
            <td className="num">
              <b>{formatInr(credits)}</b>
            </td>
            <td />
          </tr>
        </tbody>
      </table>
      {(canSubmit || canPost || canDecide) && (
        <div className="row" style={{ padding: '8px 12px' }}>
          <ErrorNote error={act.error} />
          {canSubmit && (
            <button
              className="primary"
              disabled={act.isPending}
              onClick={() => act.mutate({ path: `/journals/${j.id}/submit` })}
            >
              {j.status === 'rejected' ? 'Submit again' : 'Submit for approval'}
            </button>
          )}
          {canPost && (
            <button
              className="primary"
              disabled={act.isPending}
              onClick={() => act.mutate({ path: `/approvals/manual-journal/${j.id}/post` })}
            >
              Post to the books
            </button>
          )}
          {canDecide && (
            <>
              <button
                className="primary"
                disabled={act.isPending}
                onClick={() =>
                  act.mutate({
                    path: `/approvals/manual-journal/${j.id}/decide`,
                    body: { approve: true },
                  })
                }
              >
                Approve
              </button>
              <input
                aria-label="Why it is rejected"
                placeholder="Why is it rejected?"
                value={sendBack}
                onChange={(e) => setSendBack(e.target.value)}
              />
              <button
                disabled={act.isPending || !sendBack.trim()}
                onClick={() =>
                  act.mutate({
                    path: `/approvals/manual-journal/${j.id}/decide`,
                    body: { approve: false, reason: sendBack },
                  })
                }
              >
                Reject
              </button>
            </>
          )}
        </div>
      )}
    </Card>
  )
}
